import CanvasRenderer from './CanvasRenderer';
import React, { useEffect, useRef, useState } from 'react';

const cap = 9999.999;

let getX = (y, slope, yIntercept) => (y - yIntercept) / slope;
let getY = (x, slope, yIntercept) => (slope * x) + yIntercept;

export default function SelectionApplet() {
    const [tab, setTab] = useState('inputs');
    const [axis, setAxis] = useState('y');
    const [inputs, setInputs] = useState(DEFAULT_DATA);
    const [equations, setEquations] = useState(null);
    const [window, setWindow] = useState({
        minX: Math.min(inputs.group1.muX - (4 * inputs.group1.sigmaX), inputs.group2.muX - (4 * inputs.group2.sigmaX)),
        minY: Math.min(inputs.group1.muY - (4 * inputs.group1.sigmaY), inputs.group2.muY - (4 * inputs.group2.sigmaY)),
        maxX: Math.max(inputs.group1.muX + (4 * inputs.group1.sigmaX), inputs.group2.muX + (4 * inputs.group2.sigmaX)),
        maxY: Math.max(inputs.group1.muY + (4 * inputs.group1.sigmaY), inputs.group2.muY + (4 * inputs.group2.sigmaY))
    });

    const [yAxisSlider, setYAxisSlider] = useState(2.72);
    const [xAxisSlider, setXAxisSlider] = useState(52.8);

    const calcWindow = () => {
        setWindow({
            minX: Math.min(Number(inputs.group1.muX) - (4 * Number(inputs.group1.sigmaX)), Number(inputs.group2.muX) - (4 * Number(inputs.group2.sigmaX))),
            minY: Math.min(Number(inputs.group1.muY) - (4 * Number(inputs.group1.sigmaY)), Number(inputs.group2.muY) - (4 * Number(inputs.group2.sigmaY))),
            maxX: Math.max(Number(inputs.group1.muX) + (4 * Number(inputs.group1.sigmaX)), Number(inputs.group2.muX) + (4 * Number(inputs.group2.sigmaX))),
            maxY: Math.max(Number(inputs.group1.muY) + (4 * Number(inputs.group1.sigmaY)), Number(inputs.group2.muY) + (4 * Number(inputs.group2.sigmaY)))
        });
    }

    const setupEquation = (group) => {
        let slope = group.rho * (group.sigmaY / group.sigmaX);
        let yIntercept = group.muY - (slope * group.muX);
        return {
            slope,
            yIntercept
        }
    }

    const setupEquations = () => {
        let graphArea = {
            y1: setupEquation(inputs.group1),
            y2: setupEquation(inputs.group2),
            y3: setupEquation(inputs.total_population)
        };

        setEquations(graphArea);
    }

    useEffect(() => {
        calcWindow();
        setupEquations();
    }, [inputs]);

    return (
        <div className='row'>
            <div id="canvas">
                {axis == 'y' && <input type="range" step={0.01} min={window.minY} max={window.maxY} id="vertical_slider" value={yAxisSlider} onChange={((ev) => setYAxisSlider(ev.target.value))}></input>}
                <CanvasRenderer sliders={{ active: axis, x: xAxisSlider, y: yAxisSlider }} equations={equations} inputs={inputs} window={window} />
                <br />
                {axis == 'x' && <input type="range" step={0.01} min={window.minX} max={window.maxX} id="horizontal_slider" value={xAxisSlider} onChange={((ev) => setXAxisSlider(ev.target.value))}></input>}

            </div>

            <div className='col'>
                <div className='m5 p10'>
                    <div id="tabs">
                        <button className={`br bc3 p10 hb1 ${tab === 'inputs' ? 'b3' : 'b2'}`} onClick={() => setTab('inputs')}>Inputs</button>
                        <button className={`br bc3 p10 hb1 ${tab === 'outputs' ? 'b3' : 'b2'}`} onClick={() => setTab('outputs')}>Outputs</button>
                        <button className={`bc1 p10 hb1 ${tab === 'graphics' ? 'b3' : 'b2'}`} onClick={() => setTab('graphics')}>Graphics Default</button>
                    </div>

                    <div id="tab_bodies" className='col p10 b3'>
                        {(() => {
                            switch (tab) {
                                case 'inputs':
                                    return <TabInputs inputs={inputs} onChange={(result) => {
                                        setInputs({ ...inputs, ...result });
                                        setupEquations();
                                    }}></TabInputs>;
                                case 'outputs':
                                    return <TabOutputs inputs={inputs} equations={equations} window={window} sliders={{ active: axis, x: xAxisSlider, y: yAxisSlider }}></TabOutputs>;
                                case 'graphics':
                                    return (
                                        <div className='col' style={{ display: 'inline' }}>
                                            &nbsp;The default maximums/minimums for the X axis <br />&nbsp; are found by taking the larger/smaller of <br />&nbsp; &#956;<sub>X1</sub> +/- (4 * &#963;<sub>X1</sub>) and &#956;<sub>X2</sub> +/- (4 * &#963;<sub>X2</sub>). <br /><br /> &nbsp;The default maximums/minimums for the Y axis <br />&nbsp;are found by taking the larger/smaller of <br />&nbsp;&#956;<sub>Y1</sub> +/- (4 * &#963;<sub>Y1</sub>) and &#956;<sub>Y2</sub> +/- (4 * &#963;<sub>Y2</sub>).<br /><br />&nbsp;If you would like to change the values please <br />&nbsp;include new values below.

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'self-end' }}>
                                                <span>Minimum value for X:</span> <input type='text' value={window.minX} onChange={(ev) => setWindow({ ...window, minX: ev.target.value })} />
                                                <span>Maximum value for X:</span> <input type='text' value={window.maxX} onChange={(ev) => setWindow({ ...window, maxX: ev.target.value })} />
                                                <span>Minimum value for Y:</span> <input type='text' value={window.minY} onChange={(ev) => setWindow({ ...window, minY: ev.target.value })} />
                                                <span>Maximum value for Y:</span> <input type='text' value={window.maxY} onChange={(ev) => setWindow({ ...window, maxY: ev.target.value })} />
                                            </div>

                                        </div>
                                    );
                                default: return null;
                            }
                        })()}
                    </div>
                </div>

                <div className='p10 m5'>
                    <div id="tabs">
                        <button className={`br bc3 p10 hb1 ${axis === 'x' ? 'b3' : 'b2'}`} onClick={() => setAxis('x')}>X Axis</button>
                        <button className={`bc3 p10 hb1 ${axis === 'y' ? 'b3' : 'b2'}`} onClick={() => setAxis('y')}>Y Axis</button>
                    </div>
                    {
                        axis == 'x' ? (
                            <div className='p10 b3'>
                                <span>Common Line Selection cutoff <input type='text' value={xAxisSlider} onChange={(ev) => setXAxisSlider(ev.target.value)} /> </span>
                                <br />
                                <button className='br bb bt bl bc3 p10 hb1 b4'>X Axis</button>
                            </div>
                        ) : (
                            <div className='p10 b3 row'>
                                <div className='col'>
                                    <span className='m5'>y: <input type="text" value={yAxisSlider} onChange={(ev) => setYAxisSlider(ev.target.value)} /></span>
                                    <button className='br bb bt bl bc3 p10 hb1 b4'>Y Axis</button>
                                </div>
                                <div className='col tar fg m5c'>
                                    <span>x<sub>1</sub>: <span className='pl10 pr10 b4'>{equations && getX(yAxisSlider, equations.y1.slope, equations.y1.yIntercept).toFixed(3)}</span></span>
                                    <span>x<sub>2</sub>: <span className='pl10 pr10 b4'>{equations && getX(yAxisSlider, equations.y2.slope, equations.y2.yIntercept).toFixed(3)}</span></span>
                                    <span>x<sub>common</sub>: <span className='pl10 pr10 b4'>{equations && getX(yAxisSlider, equations.y3.slope, equations.y3.yIntercept).toFixed(3)}</span></span>
                                </div>
                            </div>
                        )
                    }
                </div>

                {equations && (<div className='col p10c'>
                    <span style={{ color: 'red' }}>{`Group 1: ${Number(equations.y1.yIntercept.toFixed(4))} + ${Number(equations.y1.slope.toFixed(4))}x`}</span>
                    <span style={{ color: 'magenta' }}>{`Group 2: ${Number(equations.y2.yIntercept.toFixed(4))} + ${Number(equations.y2.slope.toFixed(4))}x`}</span>
                    <span style={{ color: '#66f' }}>{`Common Line: ${Number(equations.y3.yIntercept.toFixed(4))} + ${Number(equations.y3.slope.toFixed(4))}x`}</span>
                </div>)}
            </div>
        </div>
    );
};


function TabInputs({ inputs, onChange }) {

    return (
        <div id="inputs">
            <span className='bold'>Total Population Parameters</span>
            <GroupParams group={inputs.total_population} onChange={onChange} />
            <div>
                <span className='bold'>Group-Specific Parameters</span>
                <div className='row'>
                    <GroupParams group={inputs.group1} title={"Group 1"} onChange={onChange} />
                    <GroupParams group={inputs.group2} title={"Group 2"} onChange={onChange} />
                </div>
            </div>
        </div>
    )
}

function TabOutputs({ equations, inputs, window, sliders }) {

    let yStar;
    if (sliders.active == 'x')
        yStar = getY(sliders.x, equations.y3.slope, equations.y3.yIntercept);
    else
        yStar = sliders.y;

    let x1Star = (yStar - equations.y1.yIntercept) / equations.y1.slope;
    let x2Star = (yStar - equations.y2.yIntercept) / equations.y2.slope;
    let x3Star = (yStar - equations.y3.yIntercept) / equations.y3.slope;

    // calculate zStar values
    let zStarU1 = (x1Star - inputs.group1.muX) / inputs.group1.sigmaX;
    let zStarU2 = (x2Star - inputs.group2.muX) / inputs.group2.sigmaX;
    let zStarF1 = (x3Star - inputs.group1.muX) / inputs.group1.sigmaX;
    let zStarF2 = (x3Star - inputs.group2.muX) / inputs.group2.sigmaX;

    // calculate standard normal probabilities
    let SRU1 = stdNormProb(zStarU1);
    let SRU2 = stdNormProb(zStarU2);
    let SRF1 = stdNormProb(zStarF1);
    let SRF2 = stdNormProb(zStarF2);

    // calculate adverse impact ratios
    let AIF = SRF1 / SRF2;
    let AIU = SRU1 / SRU2;

    let y1Star = equations.y1.yIntercept + (equations.y1.slope * x3Star);
    let y2Star = equations.y2.yIntercept + (equations.y2.slope * x3Star);

    let group1Prob = "Group 1: Prob()";
    let group2Prob = "Group 2: Prob()";
    let group1ProbVal = '0%';
    let group2ProbVal = '0%';

    // calculate and display false positives/negatives
    // Group 1
    if (zStarU1 < zStarF1) {
        group1Prob = "Group 1:  Prob(false negative):  ";
        group1ProbVal = ((SRU1 - SRF1) * 100).toFixed(2) + "%";
    } else if (zStarU1 > zStarF1) {
        group1Prob = "Group 1:  Prob(false positive):  ";
        group1ProbVal = ((SRF1 - SRU1) * 100).toFixed(2) + "%";
    }

    if (zStarU2 < zStarF2) {
        group2Prob = "Group 2:  Prob(false negative):  ";
        group2ProbVal = ((SRU2 - SRF2) * 100).toFixed(2) + "%";
    } else if (zStarU2 > zStarF2) {
        group2Prob = "Group 2:  Prob(false positive):  ";
        group2ProbVal = ((SRF2 - SRU2) * 100).toFixed(2) + "%";
    }

    return (
        <div id="outputs" className='col p10'>
            <span className='bold'>Desired Performance: </span>
            <span className='pr10 pl10 b4'>{Number(yStar).toFixed(2)}</span>
            <span></span>

            <span className='bold'>Common Line results:</span>
            <span></span>
            <span></span>

            <span className='tar'>Adverse Impact: </span>
            <span className='pr10 pl10 b4'>{(AIF * 100).toFixed(2) + "%"}</span>
            <span></span>

            <span className='tar'>Selection Cutoff: </span>
            <span className='pr10 pl10 b4'>{x3Star.toFixed(3)}</span>
            <span></span>

            <span></span>
            <span className='bold'>Group 1</span>
            <span className='bold'>Group 2</span>

            <span className='tar'>Selection Ratio:</span>
            <span className='pr10 pl10 b4'>{(SRF1 * 100).toFixed(2) + "%"}</span>
            <span className='pr10 pl10 b4'>{(SRF2 * 100).toFixed(2) + "%"}</span>

            <span className='tar'>Actual Performance:</span>
            <span className='pr10 pl10 b4'>{y1Star.toFixed(3)}</span>
            <span className='pr10 pl10 b4'>{y2Star.toFixed(3)}</span>

            <span className='gspan2 tar bold'>{group1Prob}</span>
            <span className='pr10 pl10 b4'>{group1ProbVal}</span>

            <span className='gspan2 tar bold'>{group2Prob}</span>
            <span className='pr10 pl10 b4'>{group2ProbVal}</span>

            <span className='bold'>Group based results:</span>
            <span></span>
            <span></span>

            <span className='tar'>Adverse Impact: </span>
            <span className='pr10 pl10 b4'>{(AIU * 100).toFixed(2) + "%"}</span>
            <span></span>

            <span></span>
            <span className='bold'>Group 1</span>
            <span className='bold'>Group 2</span>

            <span className='tar'>Selection Ratio:</span>
            <span className='pr10 pl10 b4'>{(SRU1 * 100).toFixed(2) + "%"}</span>
            <span className='pr10 pl10 b4'>{(SRU2 * 100).toFixed(2) + "%"}</span>

            <span className='tar'>Selection Cutoff: </span>
            <span className='pr10 pl10 b4'>{x1Star.toFixed(3)}</span>
            <span className='pr10 pl10 b4'>{x2Star.toFixed(3)}</span>
        </div>
    )
}

function GroupParams({ group, title, onChange }) {
    const [muX, setMuX] = useState(group.muX);
    const [muY, setMuY] = useState(group.muY);
    const [sigmaX, setSigmaX] = useState(group.sigmaX);
    const [sigmaY, setSigmaY] = useState(group.sigmaY);
    const [rho, setRho] = useState(group.rho);

    useEffect(() => {
        console.log("OC")
        onChange({
            [group.name]: {
                name: group.name,
                number: group.number,
                muX,
                muY,
                sigmaX,
                sigmaY,
                rho
            }
        });
    }, [muX, muY, sigmaX, sigmaY, rho]);

    return (
        <div className='p10 m5'>
            <span className='bold'>{title || ''}</span>
            <InputField label={<span>μ<sub>X{group.number || ""}</sub></span>} value={muX} onChange={setMuX} />
            <InputField label={<span>μ<sub>Y{group.number || ""}</sub></span>} value={muY} onChange={setMuY} />
            <InputField label={<span>σ<sub>X{group.number || ""}</sub></span>} isSigma value={sigmaX} onChange={setSigmaX} />
            <InputField label={<span>σ<sub>Y{group.number || ""}</sub></span>} isSigma value={sigmaY} onChange={setSigmaY} />
            <InputField label={<span>ρ<sub>{group.number || ""}</sub></span>} isRho value={rho} onChange={setRho} />
        </div>
    )
}

function InputField({ label, value, onChange, isSigma, isRho }) {
    let invalid = isNaN(Number(value)) ? 'This is not a valid number' : '';

    if (isSigma && value <= 0) invalid = 'All σ values must exceed 0';
    if (isRho && (value < 0 || value > 1)) invalid = 'ρ must be between 0 and 1 (inclusive)';

    return (
        <div className='col'>
            <div>
                <span style={{ width: '40px', display: 'inline-block' }}>{label}</span>
                <input type="text" value={value} onChange={(val) => { console.log(val); onChange(val.target.value) }} />
            </div>
            <span className={`cred ${invalid ? '' : 'nodisplay'}`}>{invalid}</span>
        </div>
    )
}

function stdNormProb(zStar) {
    let temp1 = (Math.exp(-(Math.pow(zStar, 2)) / 2)) / (Math.sqrt(2 * Math.PI));
    let temp2 = 1 / (1 + .2316419 * Math.abs(zStar));
    let temp3 = temp1 * ((.31938153 * temp2) - (.356563782 * Math.pow(temp2, 2)) + (1.781477937 * Math.pow(temp2, 3))
        - (1.821255978 * Math.pow(temp2, 4)) + (1.330274429 * Math.pow(temp2, 5)));

    if (zStar >= 0)
        return (temp3);
    else
        return (1 - temp3);
}

const DEFAULT_DATA = {
    total_population: {
        name: "total_population",
        muX: 98.56,
        muY: 3.35,
        sigmaX: 10.41,
        sigmaY: 1.04,
        rho: 0.54
    },
    group1: {
        name: "group1",
        number: 1,
        muX: 92.8,
        muY: 2.75,
        sigmaX: 10,
        sigmaY: 1,
        rho: 0.5
    },
    group2: {
        name: "group2",
        number: 2,
        muX: 100,
        muY: 3.5,
        sigmaX: 10,
        sigmaY: 1,
        rho: 0.5
    }
};